import format from 'date-fns/format';
import isSameDay from 'date-fns/isSameDay';
import isSameMonth from 'date-fns/isSameMonth';
import isTomorrow from 'date-fns/isTomorrow';
import ru from 'date-fns/locale/ru';

/**
 * Опции форматирования интервала
 */
export interface FormatTimestampIntervalOptions {
    /** При style 'full': выводить только дату без времени */
    showOnlyDate?: boolean;
    /** 'full' — с днём недели и «Завтра»; 'short' — только время/дата*/
    style?: 'full' | 'short';
    /** При style 'short': true — «9:00 – 18:00», false — «27 января» */
    showTimeRange?: boolean;
    /** При style 'short': для многодневного интервала выводить «20 – 23 февраля» */
    showMultiDayRange?: boolean;
}

const formatTime = (date: Date) => format(date, 'HH:mm');
const formatTimeShort = (date: Date) => format(date, 'H:mm');

function formatDate(date: Date): string {
    if (isTomorrow(date)) {
        return 'Завтра';
    }

    return format(date, 'EEEEEE, dd MMMM', { locale: ru });
}

function formatShort(from: Date, to: Date, showTimeRange: boolean, showMultiDayRange: boolean): string {
    const multiDay = !isSameDay(from, to);

    if (multiDay && showMultiDayRange) {
        return isSameMonth(from, to)
            ? `${format(from, 'd', { locale: ru })} – ${format(to, 'd MMMM', { locale: ru })}`
            : `${format(from, 'd MMMM', { locale: ru })} – ${format(to, 'd MMMM', { locale: ru })}`;
    }

    return showTimeRange ? `${formatTimeShort(from)} – ${formatTimeShort(to)}` : format(from, 'd MMMM', { locale: ru });
}

/**
 * Форматирует интервал времени в читаемую строку.
 *
 * - full (по умолчанию)- развёрнутый формат с «Завтра», днём недели; опция showOnlyDate убирает время.
 *   Пример: «Завтра 10:00 - 13:00», «ср, 29 октября - чт, 30 октября».
 * - short - короткий формат; showTimeRange (время или дата), showMultiDayRange (диапазон дат для многодневного интервала).
 *   Пример: «9:00 – 18:00», «27 января» или многодневный «20 – 23 февраля».
 *
 * @kind helper
 * @param interval — интервал с полями from, to (Unix timestamp в секундах)
 * @param options — опции форматирования
 * @returns Отформатированная строка интервала
 */
export function formatTimestampInterval(
    interval: { from: number; to: number },
    options: FormatTimestampIntervalOptions = {}
): string {
    const { showOnlyDate = false, style = 'full', showTimeRange = false, showMultiDayRange = false } = options;

    const from = new Date(interval.from * 1000);
    const to = new Date(interval.to * 1000);

    if (style === 'short') {
        return formatShort(from, to, showTimeRange, showMultiDayRange);
    }

    const fromPrefix = formatDate(from);
    const toPrefix = formatDate(to);

    if (fromPrefix === toPrefix) {
        return showOnlyDate ? fromPrefix : `${fromPrefix} ${formatTime(from)} - ${formatTime(to)}`;
    }

    return showOnlyDate
        ? `${fromPrefix} - ${toPrefix}`
        : `${fromPrefix} ${formatTime(from)} - ${toPrefix} ${formatTime(to)}`;
}
